import { Link } from "react-router-dom";
import { PageHero, PageLayout } from "../components/PageShell";
import { Placeholder } from "../components/Placeholder";
import { DeanBand } from "../components/DeanBand";
import { CurriculumMap, PostgraduateMap } from "../components/signatures";
import { ROUTES } from "../lib/routes";
import { DEPARTMENTS } from "../lib/departments";
import { DEANS, deanPhoto } from "../lib/deans";
import { useSectionTabs } from "../lib/useSectionTabs";
import { usePostgraduateProgrammes } from "../lib/publishedProgram";
import { useDepartmentHeadcount } from "../lib/mergedFaculty";
import { useDepartmentLabCounts, useDepartmentTaglines } from "../lib/publishedDepartmentExtras";

/** Dean Academics and Associate Dean Academics — every seat this office has, per deans.ts. */
const ACADEMICS_DEANS = DEANS.filter((d) => d.role.includes("Academics"));


export default function Academics() {
  const postgraduate = usePostgraduateProgrammes();
  // Each department card's line is its published tagline (the local intro only where none is published).
  const taglineOf = useDepartmentTaglines();
  // The undergraduate and science cards' counts are the department page's own: its merged roster, and its published lab count.
  const headcountOf = useDepartmentHeadcount();
  const labCountOf = useDepartmentLabCounts();
  // Scroll-spy: highlight the Programs nav item for the stream currently in view.
  const activeId = useSectionTabs(["undergraduate", "postgraduate", "sciences", "research"]);
  return (
    <>
      <PageHero
        variant="hero-navy hero-compact"
        fullWidthTitle
        eyebrow="Academics"
        title="Programs grounded in rigor. Built for what's next."
        lead={`Thirteen undergraduate branches, ${postgraduate.length} postgraduate programmes and four applied-science departments — all VTU-affiliated and, for the accredited programmes, assessed by the NBA.`}
        crumbs={[{ label: "Academics" }]}
        image={null}
      />
      <div className="page-tabs">
      <PageLayout
        side={[
          { title: "Programs", items: [
            { label: "Undergraduate", to: "#undergraduate", active: activeId === "undergraduate" },
            { label: "Postgraduate", to: "#postgraduate", active: activeId === "postgraduate" },
            { label: "Applied Sciences", to: "#sciences", active: activeId === "sciences" },
            { label: "Research / Ph.D.", to: "#research", active: activeId === "research" },
          ]},
          { title: "Resources", items: [
            { label: "Academic Calendar", to: ROUTES.academicCalendar },
            { label: "Examinations", to: ROUTES.examinations },
            { label: "Departments", to: ROUTES.faculty },
          ]},
        ]}
      >
        <h2 id="undergraduate">Undergraduate (B.E.) — 13 branches</h2>
        <p>Four years, eight semesters, affiliated to VTU Belagavi. Admission is through KCET, COMEDK or the management quota.</p>
        <div className="dept-grid">
          {DEPARTMENTS.filter((d) => d.kind === "engineering").map((d) => (
            <Link key={d.slug} to={`${ROUTES.department}/${d.slug}`} className="dept-card">
              <div className="dept-code">B.E. · {d.code}</div>
              <h3>{d.name}</h3>
              <p>{taglineOf(d)}</p>
              <div className="meta">
                <span>{headcountOf(d) || "—"} staff</span>
                {labCountOf(d) != null && <span>{labCountOf(d)} labs</span>}
              </div>
            </Link>
          ))}
        </div>

        <CurriculumMap />

        <h2 id="postgraduate">Postgraduate (M.Tech / MCA)</h2>
        <p>{postgraduate.length} postgraduate programmes, all VTU-affiliated, with provisions for sponsored, full-time and part-time candidates.</p>
        {/* The published postgraduate programmes (name, award, duration, faculty
            count), plus any PG department webadmin has not published a programme
            for — see usePostgraduateProgrammes. The blurb is the department's
            own text: the programmes publish none. */}
        <div className="dept-grid">
          {postgraduate.map((p) => (
            <Link key={p.department.slug} to={`${ROUTES.department}/${p.department.slug}`} className="dept-card">
              <div className="dept-code">{p.awardLabel}</div>
              <h3>{p.name}</h3>
              <p>{taglineOf(p.department)}</p>
              <div className="meta">
                {p.duration && <span>{p.duration}</span>}
                <span>{p.facultyCount || "—"} staff</span>
              </div>
            </Link>
          ))}
        </div>

        <PostgraduateMap />

        <h2 id="sciences">Applied Sciences &amp; humanities</h2>
        <p>Four departments carry the first-year core and the science teaching that every branch depends on.</p>
        <div className="dept-grid">
          {DEPARTMENTS.filter((d) => d.kind === "science").map((d) => (
            <Link key={d.slug} to={`${ROUTES.department}/${d.slug}`} className="dept-card">
              <div className="dept-code">{d.code}</div>
              <h3>{d.name}</h3>
              <p>{taglineOf(d)}</p>
              <div className="meta">
                <span>{headcountOf(d) || "—"} staff</span>
              </div>
            </Link>
          ))}
        </div>

        <h2 id="research">Research</h2>
        <p>Fifteen VTU-recognised research centres across CSE, ECE, EEE, Mechanical, Civil, Chemical, Textile, Mathematics, Chemistry, Physics and MCA — anchoring Ph.D. work and faculty research across the institute.</p>

      </PageLayout>
      </div>

      {/* Same shape as /admissions and /examinations, but Academics has three
          seats rather than one — all three share a single card instead of
          stacking three near-identical bands. */}
      <DeanBand
        kicker="Office of Academics"
        title="Curriculum, scheduling and academic policy across every programme."
        people={ACADEMICS_DEANS.map((d) => ({ name: d.name, role: d.role, photo: deanPhoto(d) }))}
      >
        The academics office sets curriculum and the academic calendar, coordinates
        scheduling across departments, and administers the institute's autonomous
        academic framework for every UG and PG programme.
      </DeanBand>
    </>
  );
}
